import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Typography, Space, Alert } from 'antd';
import dayjs from 'dayjs';
import { bdoApi } from '../../../api/client';
import type { BdoRange, BdoSalesRow } from '../types';
import { formatGrams, formatMg } from '../types';

const { Text } = Typography;

interface Props {
    range: BdoRange;
    isMobile?: boolean;
}

export default function BdoSalesTab({ range, isMobile }: Props) {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);

    const { data, isLoading } = useQuery({
        queryKey: ['bdo-sales', range.year, range.from, range.to, page, limit],
        queryFn: () =>
            bdoApi
                .getSales({
                    year: range.year,
                    from: range.from,
                    to: range.to,
                    page,
                    limit,
                })
                .then((res) => res.data),
    });

    const sourceColor = (source: string) => {
        if (source.includes('BRAK')) return 'red';
        if (source.includes('własna')) return 'orange';
        if (source.includes('FEFO')) return 'blue';
        return 'green';
    };

    const columns = [
        {
            title: 'Data',
            dataIndex: 'date',
            key: 'date',
            width: 110,
            render: (d: string) => dayjs(d).format('DD.MM.YYYY'),
        },
        {
            title: 'Zamówienie',
            dataIndex: 'orderNumber',
            key: 'orderNumber',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Produkt',
            dataIndex: 'productName',
            key: 'productName',
            ellipsis: true,
            render: (name: string, record: BdoSalesRow) => (
                <div>
                    <div>{name}</div>
                    {record.sku && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {record.sku}
                        </Text>
                    )}
                </div>
            ),
        },
        {
            title: 'Ilość',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right' as const,
            width: 80,
        },
        {
            title: 'Partia',
            dataIndex: 'batchLotNumber',
            key: 'batchLotNumber',
            width: 130,
            render: (lot: string) => lot || <Text type="secondary">—</Text>,
        },
        {
            title: 'Źródło kompozycji',
            dataIndex: 'compositionSource',
            key: 'compositionSource',
            width: 190,
            render: (source: string) => (
                <Tag color={sourceColor(source)}>{source}</Tag>
            ),
        },
        {
            title: 'Masa [g]',
            dataIndex: 'totalGrams',
            key: 'totalGrams',
            align: 'right' as const,
            width: 110,
            render: (v: number) => formatGrams(v),
        },
        {
            title: 'Masa [Mg]',
            dataIndex: 'totalMg',
            key: 'totalMg',
            align: 'right' as const,
            width: 120,
            render: (v: number) => <Text strong>{formatMg(v)}</Text>,
        },
    ];

    return (
        <Card size="small" title="Sprzedaż — pozycje zamówień">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {(data?.rows || []).some((r: BdoSalesRow) => !r.productId) && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Część pozycji nie ma powiązanego produktu — ich masa wynosi 0."
                    />
                )}
                <Table
                    dataSource={data?.rows || []}
                    columns={columns}
                    rowKey={(record: BdoSalesRow, index?: number) =>
                        `${record.orderId}-${record.productId}-${index}`
                    }
                    loading={isLoading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={{ x: 1000 }}
                    pagination={{
                        current: page,
                        pageSize: limit,
                        total: data?.total || 0,
                        showSizeChanger: !isMobile,
                        pageSizeOptions: ['25', '50', '100', '200'],
                        showTotal: (total) => `${total} pozycji`,
                        onChange: (nextPage, nextSize) => {
                            setPage(nextPage);
                            if (nextSize && nextSize !== limit) {
                                setLimit(nextSize);
                                setPage(1);
                            }
                        },
                    }}
                />
            </Space>
        </Card>
    );
}
